import { useMemo } from 'react';
import { cn } from '@/lib/cn';
import type { LabelView } from '@/types/api';

/**
 * Selectable multi-choice chip grid. Backs the onboarding interest picker,
 * the ReaderEdit labels section, and eventually the admin label multi-selects.
 *
 * Toggling is controlled — the parent owns the selected `Set<string>` of
 * label ids. We render a plain button grid (vs. checkboxes styled as chips)
 * because the chips are large targets and screen readers announce toggled
 * state via `aria-pressed`.
 */
export interface LabelChipGridProps {
  labels: LabelView[];
  selectedIds: ReadonlySet<string>;
  onToggle: (labelId: string) => void;
  /** Optional max selection — once hit, unchecked chips become disabled. */
  maxSelected?: number;
  /** Shown when the `labels` array is empty (e.g. loading finished but no data). */
  emptyLabel?: string;
  className?: string;
  /** Visual density. `cozy` (default) is the onboarding / ReaderEdit size. */
  size?: 'cozy' | 'compact';
}

export function LabelChipGrid({
  labels,
  selectedIds,
  onToggle,
  maxSelected,
  emptyLabel = 'Nothing to pick from yet.',
  className,
  size = 'cozy',
}: LabelChipGridProps) {
  const sorted = useMemo(
    () => [...labels].sort((a, b) => a.alias.localeCompare(b.alias)),
    [labels],
  );

  if (sorted.length === 0) {
    return <p className="text-[13px] text-or-slate-500">{emptyLabel}</p>;
  }

  const atMax = typeof maxSelected === 'number' && selectedIds.size >= maxSelected;

  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {sorted.map((label) => {
        const isSelected = selectedIds.has(label.id);
        const isDisabled = !isSelected && atMax;
        return (
          <button
            key={label.id}
            type="button"
            aria-pressed={isSelected}
            disabled={isDisabled}
            onClick={() => onToggle(label.id)}
            className={cn(
              'rounded-full border font-sans font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-500/40',
              size === 'cozy' ? 'px-3.5 py-1.5 text-[13px]' : 'px-2.5 py-1 text-[12px]',
              isSelected
                ? 'border-transparent bg-or-orange-500 text-white shadow-sm'
                : 'border-or-slate-200 bg-white text-or-slate-700 hover:border-or-orange-300 hover:bg-or-orange-50',
              isDisabled &&
                'cursor-not-allowed opacity-50 hover:border-or-slate-200 hover:bg-white',
            )}
          >
            {label.alias}
          </button>
        );
      })}
    </div>
  );
}
